import { getFeaturePanelDefinition } from "../feature-panel-registry.ts";
import type { RuntimeFeatureModule } from "../runtime-feature-registry.ts";
import {
  applyTextEffectValue,
  resetTextEffectValue,
} from "../shared/page-text-effects.ts";

const variable = "--migrosone-a11y-font-family";
const attribute = "data-migrosone-a11y-font-family";
const maxFontOptions = 8;
const safeFontPattern = /^[A-Za-z0-9 "'_-]+(?:,\s*[A-Za-z0-9 "'_-]+)*$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readFontList(value: unknown) {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === "string");
  }

  if (typeof value === "string") {
    return value.split(",");
  }

  return [];
}

function isSafeFontValue(value: string) {
  return value.trim() !== "" && safeFontPattern.test(value);
}

function parseFontConfig(config: unknown) {
  if (!isRecord(config)) {
    return { allowedFonts: [] as string[], defaultFont: null as string | null };
  }

  const allowedFonts = [
    ...readFontList(config.allowedFonts),
    ...readFontList(config.fontOptions),
  ]
    .map((font) => font.trim())
    .filter(isSafeFontValue)
    .slice(0, maxFontOptions);
  const uniqueAllowedFonts = Array.from(new Set(allowedFonts));
  const defaultFont =
    typeof config.defaultFont === "string" && isSafeFontValue(config.defaultFont)
      ? config.defaultFont.trim()
      : uniqueAllowedFonts[0] ?? null;

  return {
    allowedFonts: uniqueAllowedFonts,
    defaultFont:
      defaultFont && uniqueAllowedFonts.includes(defaultFont)
        ? defaultFont
        : uniqueAllowedFonts[0] ?? null,
  };
}

function resolveFontValue(config: unknown, preference: unknown) {
  const parsed = parseFontConfig(config);
  if (parsed.allowedFonts.length === 0) {
    return null;
  }

  if (
    typeof preference === "string" &&
    parsed.allowedFonts.includes(preference.trim())
  ) {
    return preference.trim();
  }

  return parsed.defaultFont;
}

const definition = getFeaturePanelDefinition("font-family");

if (!definition) {
  throw new Error("Missing font-family feature definition");
}

export const fontFamilyFeatureModule: RuntimeFeatureModule = {
  key: "font-family",
  definition,
  isSupported({ config, document }) {
    return (
      Boolean(document.head && document.documentElement) &&
      parseFontConfig(config).allowedFonts.length > 0
    );
  },
  renderControl({ config, document, onPreferenceChange, preference }) {
    const parsed = parseFontConfig(config);
    if (parsed.allowedFonts.length === 0) {
      throw new Error("No allowed fonts configured");
    }

    const wrapper = document.createElement("div");
    const label = document.createElement("label");
    const select = document.createElement("select");
    const selected = resolveFontValue(config, preference);

    wrapper.className = "migrosone-a11y-widget-feature-control";
    label.textContent = "Yazı tipi";

    for (const font of parsed.allowedFonts) {
      const option = document.createElement("option");
      option.value = font;
      option.textContent = font;
      select.append(option);
    }

    select.value = selected ?? parsed.allowedFonts[0] ?? "";
    select.addEventListener("change", () => {
      onPreferenceChange(select.value);
    });

    wrapper.append(label, select);
    return wrapper;
  },
  apply({ config, document, preference }) {
    const value = resolveFontValue(config, preference);
    if (!value) {
      resetTextEffectValue({ attribute, document, variable });
      return;
    }

    applyTextEffectValue({
      attribute,
      document,
      value,
      variable,
    });
  },
  reset({ config, document }) {
    resetTextEffectValue({ attribute, document, variable });
    const value = resolveFontValue(config, undefined);
    if (value) {
      applyTextEffectValue({
        attribute,
        document,
        value,
        variable,
      });
    }
  },
};
